/* eslint-disable @typescript-eslint/no-explicit-any */
import styled from "@emotion/styled";
import { Page } from "@/components/Page";
import { absoluteUrl, dehydrateForProps } from "@/utils/ssg";
import { DehydratedState, QueryClient } from "@tanstack/react-query";
import { GetStaticPaths, GetStaticProps } from "next";
import Head from "next/head";

import {
  ArticleCategoryRootCategory,
  apiArticlesGetCollection,
  apiArticlesdetailGetCollection,
  getApiArticlesdetailGetCollectionQueryKey,
} from "@fllite/api";
import { Article } from "@fllite/ui/components";
import { Layout } from "@fllite/ui/components";

const StyledPage = styled(Page)`
  background: #edf0f4;
`;

// Everything the <Head> needs, lifted out of the article so the meta tags do
// not depend on the client-side query resolving.
interface ArticleMeta {
  slug: string;
  title: string;
  metaTitle: string | null;
  metaDescription: string | null;
  mainImageUrl: string | null;
  publishedAt: string | null;
  updatedAt: string | null;
}

interface Props {
  article: ArticleMeta;
  dehydratedState: DehydratedState;
}

const BlogDetail = ({ article }: Props) => {
  const url = absoluteUrl(`/blog/${article.slug}`);
  const title = article.metaTitle ?? article.title;
  const { metaDescription } = article;

  return (
    <>
      <Head>
        <title>{`${title} | Fllite`}</title>
        {metaDescription && (
          <meta name="description" content={metaDescription} />
        )}
        <link rel="canonical" href={url} />

        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="Fllite" />
        <meta property="og:locale" content="en_US" />
        <meta property="og:title" content={title} />
        {metaDescription && (
          <meta property="og:description" content={metaDescription} />
        )}
        <meta property="og:url" content={url} />
        {article.mainImageUrl && (
          <>
            <meta property="og:image" content={article.mainImageUrl} />
            <meta property="og:image:alt" content={article.title} />
          </>
        )}
        {article.publishedAt && (
          <meta
            property="article:published_time"
            content={article.publishedAt}
          />
        )}
        {article.updatedAt && (
          <meta property="article:modified_time" content={article.updatedAt} />
        )}

        <meta
          name="twitter:card"
          content={article.mainImageUrl ? "summary_large_image" : "summary"}
        />
        <meta name="twitter:title" content={title} />
        {metaDescription && (
          <meta name="twitter:description" content={metaDescription} />
        )}
        {article.mainImageUrl && (
          <meta name="twitter:image" content={article.mainImageUrl} />
        )}
      </Head>
      <Layout allowOverflow>
        <StyledPage
          hasFooter
          hasSubscriptionForm={false}
          hasNavigationBackground
        >
          <Article isBlog slug={article.slug} />
        </StyledPage>
      </Layout>
    </>
  );
};

// Blocking fallback: articles published between deploys are rendered on first
// request and cached, rather than 404ing until the next build.
export const getStaticPaths: GetStaticPaths = async () => {
  try {
    const articles = (await apiArticlesGetCollection({
      rootCategory: ArticleCategoryRootCategory.blog,
      itemsPerPage: 1000,
    })) as any[];

    return {
      paths: articles
        .filter((article) => article?.slug)
        .map((article) => ({ params: { id: article.slug as string } })),
      fallback: "blocking",
    };
  } catch (error) {
    // A flaky API at build time should not fail the build — every path still
    // renders on demand through the blocking fallback.
    // eslint-disable-next-line no-console
    console.error("blog: failed to load slugs for getStaticPaths", error);

    return { paths: [], fallback: "blocking" };
  }
};

export const getStaticProps: GetStaticProps<Props> = async ({ params }) => {
  const slug = params?.id;

  if (typeof slug !== "string") {
    return { notFound: true };
  }

  const queryClient = new QueryClient();
  const queryParams = { slug };

  // The same call the hook makes, so the result can be dehydrated straight into
  // the cache under the key the hook will look up.
  const articles = (await apiArticlesdetailGetCollection(queryParams)) as any[];
  const article = articles?.[0];

  if (!article) {
    return { notFound: true, revalidate: 60 };
  }

  queryClient.setQueryData(
    getApiArticlesdetailGetCollectionQueryKey(queryParams),
    articles,
  );

  return {
    props: {
      article: {
        slug: article.slug,
        title: article.title ?? "",
        metaTitle: article.metaTitle || null,
        metaDescription: article.metaDescription || null,
        mainImageUrl: article.mainImageUrl || null,
        publishedAt: article.createdAt || null,
        updatedAt: article.updatedAt || null,
      },
      dehydratedState: dehydrateForProps(queryClient),
    },
    revalidate: 300,
  };
};

export default BlogDetail;
